import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  PatternlibImage,
  PatternlibList,
  PatternlibListItem,
  PatternlibAccordion,
  PatternlibAccordionItem,
  PatternlibButton,
  PatternlibIcon,
  PatternlibTextlink,
  PatternlibSpinner,
  PatternlibCard,
  PatternlibToast,
} from "@liebherr/patternlib-react";
import { useTranslation } from "react-i18next";
import Viewer from "../../components/viewer/Viewer.js";
import Nav from "../../components/nav/Nav";
import styles from "./Summary.module.css";
import configuratorStyles from "../configurator/Configurator.module.css";
import {
  copyToClipboard,
  getTableData,
  getMidentData,
  getLanguage,
} from "../../helpers/helpers";
import { apiURL, apiKey, catalog } from "../../config/api";
import KeyButton from "../../components/key-button/KeyButton";
import AddComparisonButton from "../../components/add-comparison-button/AddComparisonButton.js";
import SendButton from "../../components/send-button/SendButton.js";
import SendButtonDemo from "../../components/send-button-demo/SendButtonDemo.js";
import NotRightProductDiv from "../../components/not-right-product-div/NotRightProductDiv.js";
import InfoButton from "../../components/info-button/InfoButton.js";
import { v4 as uuidv4 } from "uuid";
// import package to generate qr code:
import QRCode from "qrcode";
// Import Icons
import diameterIcon from "../../img/liebherr-diameter-icon.svg";
import mountingIcon from "../../img/liebherr-mounting-icon.svg";
import installationLengthIcon from "../../img/liebherr-stroke-installation-length-icon.svg";
import hydraulicPortIcon from "../../img/liebherr-hydraulic-port-icon.svg";
import paintingIcon from "../../img/liebherr-painting-icon.svg";
import pistonRodVersion from "../../img/liebherr-piston-rod-version-icon.svg";
import hydzConfiguratorIcon from "../../img/liebherr-hydz-configurator-icon.svg";
import Separator from "../../components/separator/Separator.js";
import useMutationObserver from "../../hooks/useMutationObserver";
import { pushToDataLayer } from "../../helpers/helpers";

const Summary = ({ isDemoUser, userData }) => {
  const { t, i18n } = useTranslation();

  const [data, setData] = useState();
  const [productInformation, setProductInformation] = useState();
  const [part, setPart] = useState({});
  const [partkey, setPartkey] = useState();
  const [isOpen, setIsOpen] = useState();
  const [productKey, setProductKey] = useState("");

  // for generating QR Code and the URLs
  const [qrcodeUrl, setQrcodeUrl] = useState("");
  const [qrcode, setQrcode] = useState("");
  const baseUrl = "https://hydraulic-cylinder-configurator.liebherr.com"; // e. g.: https://hydraulic-cylinder-configurator.liebherr.com/

  // the useRef Hook allows to persist data between renders
  const varsetRef = useRef();
  const toastRef = useRef();

  // get params
  const { projectPath } = useParams();
  let location = useLocation();

  const path = "liebherr_components/" + projectPath + "_asmtab.prj";

  // specify Data to display in cards and their order.
  const cardKeys = ["D", "MT", "HUB", "MC", "SAE", "COL", "MAT"];
  const excludeKeys = ["SD", "EL", "P", "POS", "SAEINFO", "RCOL"];
  const cardIcons = {
    D: diameterIcon,
    MT: mountingIcon,
    HUB: installationLengthIcon,
    MC: hydraulicPortIcon,
    SAE: hydraulicPortIcon,
    COL: paintingIcon,
    MAT: pistonRodVersion,
  };

  const navigate = useNavigate();

  // use mutation observer to get current screen size
  const options = {
    config: { attributes: true, childList: true, subtree: true },
  };
  const target = document.documentElement;
  const attributeName = "breakpoint";
  const screen = useMutationObserver(options, target, attributeName);

  const formatDownloadStartGA = () => {
    const eventName = "format_download_start,";
    const pagePath = location.pathname;
    const pageType = "download_formats";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    let props = { pagePath, pageType, language };
    pushToDataLayer(eventName, props);
  };

  const getTable = (mident) => {
    getTableData(apiURL, path, apiKey, getLanguage(i18n.language), mident).then(
      (data) => {
        if (data !== undefined) {
          const varset = data.index.varsettransfer;
          const values = data.index.line.values;
          const partkey = data.index["LINA"];
          const path = data.index.path;
          varsetRef.current = varset;
          setPart({ path, varset });
          setData(values);

          // create a new empty object
          let productInformationData = {};

          Object.keys(values).map(function (key) {
            const obj = values[key];

            const newObj = {
              [key]: {
                current: obj["current"],
                resolvedVal: obj["resolvedVal"],
                Unit: obj["Unit"],
              },
            };
            // fill object
            Object.assign(productInformationData, newObj);
          });
          // update state with filled object
          setProductInformation(productInformationData);
          setPartkey(partkey);
        }
      }
    );
  };

  const copyKeyToClipboard = () => async () => {
    const toastItem = await copyToClipboard(partkey, t("KeyButton.message"));
    if (toastItem !== null) {
      const toast = toastRef.current;
      toast.createToastItem({
        ...toastItem,
      });
    }
  };

  /**
   * Function to generate a QR Code
   */
  const generateQRCode = () => {
    const encodedProductKey = encodeURI(productKey);
    const url = `${baseUrl}/${projectPath}?productkey=${encodedProductKey}`;
    setQrcodeUrl(url);
    QRCode.toDataURL(
      url, // set your url
      {
        // options -> see npm/qrcode
        width: 256,
        margin: 2,
      },
      (err, url) => {
        if (err) {
          return console.error(err);
        }
        setQrcode(url);
      }
    );
  };

  const getMident = (part) => {
    getMidentData(apiURL, part, apiKey, catalog).then((data) => {
      const mident = data["mident"] ? data["mident"] : null;
      if (mident === null) {
        // alert message
        const message = t("summary.partKeyError");
        const toast = toastRef.current;
        toast.createToastItem({
          itemId: "2",
          message: message,
          type: "alert",
          autoHide: false,
        });
      }
      // load configuration from mident
      getTable(mident);
      setProductKey(part);
    });
  };

  useLayoutEffect(() => {
    if (location.state !== null) {
      if (location.state.isOpen !== undefined) {
        setIsOpen(location.state.isOpen);
      }
      if (location.state.key !== undefined) {
        // function to get mident from key and then fetch table with mident
        const key = location.state.key;
        getMident(key);
        generateQRCode();
      } else {
        // alert message
        const message = t("summary.partKeyError");
        const toast = toastRef.current;
        toast.createToastItem({
          itemId: "2",
          message: message,
          type: "alert",
          autoHide: false,
        });
      }
    }
  }, []);

  useEffect(() => {
    generateQRCode();
  }, [productKey]);

  const card = (obj, key, obj2, customLabel, custom) => {
    return (
      <div key={key} className={styles.cardWrapper}>
        <PatternlibCard width="100%" height="100%" no-states>
          <div slot="card-content" key={key + "WRAPPER"}>
            <PatternlibImage
              src={cardIcons[key] ? cardIcons[key] : hydzConfiguratorIcon}
              width="64px"
              height="64px"
            ></PatternlibImage>
            <p className={styles.label}>
              {customLabel === undefined ? obj["desc"] : customLabel}
            </p>
            <p>
              {obj2 === undefined
                ? custom
                  ? custom
                  : obj["resolvedVal"] + " " + obj["Unit"]
                : obj["resolvedVal"] +
                  "/" +
                  obj2["resolvedVal"] +
                  " " +
                  obj["Unit"]}
            </p>
          </div>
        </PatternlibCard>
      </div>
    );
  };

  const cardLoader = () =>
    cardKeys.map(function (key) {
      const obj = data[key];

      // summerize values in one card for specific keys
      if (obj !== undefined) {
        switch (key) {
          case "D":
            return card(obj, key, data["SD"], t("summary.pistonRod"));
          case "HUB":
            return card(data["EL"], key, obj, t("summary.length"));
          case "SAE":
            if (projectPath === "series380") {
              return card(obj, key, undefined, t("summary.sae"));
            } else {
              return card(obj, key);
            }
          case "COL":
            return card(
              obj,
              key,
              undefined,
              t("summary.paint"),
              obj["current"] === "RXXXX"
                ? `${t("summary.rcol") + " " + data["RCOL"]["resolvedVal"]}`
                : undefined
            );
          default:
            return card(obj, key);
        }
      }
    });

  const listLoader = () => {
    let listData = [];
    Object.keys(data).map(function (key) {
      const obj = data[key];
      if (obj && !cardKeys.includes(key) && !excludeKeys.includes(key)) {
        if (
          obj["status"] === "static" ||
          obj["status"] === "" ||
          obj["status"] === "ranged" ||
          obj["status"] === "fixed"
        ) {
          listData.push({
            label: obj["desc"],
            value: obj["resolvedVal"] + " " + obj["Unit"],
          });
        }
      }
    });
    return (
      <PatternlibList
        data={JSON.stringify(listData)}
        label-width={screen === "isMobile" ? "75%" : "50%"}
      >
        {listData &&
          listData.map((item, index) => {
            return (
              <PatternlibListItem key={index}>
                <span>{item.label}</span>
                <div slot="value" className={styles.listValue}>
                  {item.value}
                </div>
              </PatternlibListItem>
            );
          })}
      </PatternlibList>
    );
  };

  const navigateToDownload = () => {
    formatDownloadStartGA();
    navigate("/downloads", { state: { key: { part, partkey, projectPath } } });
  };

  const createToast = (source) => (err) => {
    const toast = toastRef.current;
    if (source === "send") {
      const itemId = uuidv4();
      toast.createToastItem({
        itemId: `${itemId}`,
        message: `${t("SendButton.error") + " " + err.message}.`,
        type: "warning",
        showIcon: true,
        delay: 6000,
      });
    } else {
      const message = t("AddComparisonButton.savedToast");
      toast.createToastItem({
        itemId: "200",
        message: message,
        type: "success",
        autoHide: true,
      });
    }
  };

  const toMyconfigs = () => {
    navigate("/myconfigs");
  };

  return (
    <>
      <div className={configuratorStyles.container}>
        <div className={configuratorStyles.header}>
          <Nav
            partkey={partkey}
            projectPath={projectPath}
            activeTab="2"
            isDemoUser={isDemoUser}
          ></Nav>
        </div>
        <div className={configuratorStyles.body}>
          <div className={configuratorStyles.sidebar}>
            {partkey && (
              <div className={configuratorStyles.keyContainer}>
                <div className={configuratorStyles.keyWrapper}>
                  {screen === "isMobile" || screen === "isTablet" ? (
                    <PatternlibAccordion type="faq">
                      <PatternlibAccordionItem
                        label={t("infotexts.general.key.title")}
                      >
                        <KeyButton
                          partkey={partkey}
                          onPress={copyKeyToClipboard()}
                          title={t("infotexts.general.key.title")}
                          buttonLabel={t("KeyButton.copy")}
                        ></KeyButton>
                      </PatternlibAccordionItem>
                    </PatternlibAccordion>
                  ) : (
                    <KeyButton
                      partkey={partkey}
                      onPress={copyKeyToClipboard()}
                      title={t("infotexts.general.key.title")}
                      buttonLabel={t("KeyButton.copy")}
                    ></KeyButton>
                  )}
                </div>
              </div>
            )}
            <Viewer part={part}></Viewer>
          </div>
          <div className={configuratorStyles.content}>
            {data ? (
              <>
                <h2>{t("summary.myConfig")}</h2>
                {isDemoUser && (
                  <div className={styles.additionalsWrapper}>
                    {/* qr code for mobile devices: BAUMA-only */}

                    {qrcode !== "" && (
                      <div className={styles.qrcode}>
                        <img src={qrcode} alt="Your QR Code shows up here." />
                        {/* <a
                          href={`${baseUrl}/${projectPath}?productkey=${encodeURI(
                            productKey
                          )}`}
                        >
                          Testlink zum Produkt
                        </a> */}
                        <SendButtonDemo
                          part={partkey}
                          showModal={isOpen}
                          // productInformation contains an object with some info about the values of the product
                          productInformation={productInformation}
                          onError={createToast("send")}
                        ></SendButtonDemo>
                      </div>
                    )}
                  </div>
                )}
                <div className={styles.cards}>{cardLoader()}</div>
                <h3>{t("summary.more")}</h3>
                <div>{listLoader()}</div>
                {!isDemoUser && <Separator space="xxs"></Separator>}

                {!isDemoUser && (
                  <div className={styles.completionWrapper}>
                    <div className={styles.wrapper}>
                      <PatternlibButton
                        label={t("summary.download")}
                        type="secondary"
                        size="small"
                        onClick={navigateToDownload}
                      >
                        <PatternlibIcon
                          icon-name="download"
                          slot="icon"
                        ></PatternlibIcon>
                      </PatternlibButton>
                      <InfoButton
                        title={t("infotexts.general.downloads.title")}
                        abbreviation={"downloads"}
                        path={"general"}
                      ></InfoButton>
                    </div>
                    <SendButton
                      part={partkey}
                      showModal={isOpen}
                      productInformation={productInformation} // productInformation contains an object with some info about the values of the product
                      onError={createToast("send")}
                      locationState={{ part, partkey, projectPath }} // current location state object
                      userData={userData} // the user data
                    ></SendButton>
                  </div>
                )}
                {!isDemoUser && (
                  <div className={styles.additionalsWrapper}>
                    <div className={styles.wrapper}>
                      <AddComparisonButton
                        part={partkey}
                        path={path}
                        data={data}
                        onSave={createToast("comparison")}
                      ></AddComparisonButton>
                      <InfoButton
                        title={t("infotexts.general.comparison.title")}
                        abbreviation={"comparison"}
                        path={"general"}
                      ></InfoButton>
                    </div>
                    <PatternlibTextlink
                      onClick={toMyconfigs}
                      label={t("myconfigs.goToMyconfigs")}
                      uppercase="false"
                    />
                    <div className={styles.separator}></div>
                    <NotRightProductDiv
                      question={t("NotRightProductDiv.question")}
                      support={t("NotRightProductDiv.support")}
                    />
                  </div>
                )}
              </>
            ) : (
              <div slot="content" className={styles.modalContent}>
                <div className={styles.gridContainer}>
                  <div className={styles.item1 && styles.gridItem}>
                    <PatternlibSpinner spin></PatternlibSpinner>
                  </div>
                  <div className={styles.item2 && styles.gridItem}>
                    {t("Spinner.loadProduct")}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <PatternlibToast
        position="top-right"
        ref={toastRef}
        width={screen === "isMobile" ? "256px" : "408px"}
      ></PatternlibToast>
    </>
  );
};

export default Summary;
